import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, RefreshCw, CheckCircle2, X as XIcon, Inbox, ExternalLink,
  Mail, Building2, AlertCircle, Search, Filter, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const STATUS_OPTIONS = [
  'Asset Under Evaluation',
  'Interest Shown',
  'Meeting Requested',
  'Meeting Scheduled',
  'CDA in Progress',
  'Declined',
  'Already in Touch',
  'Awaiting Trial Data',
  'NCD Request',
  'CDA Signed',
  'Due Diligence',
  'Deal Discussion',
  'Closed - Successful',
  'Ghosted',
  'Out of Office',
  'Other',
];

const STATUS_COLORS = {
  'Declined': 'bg-rose-100 text-rose-700 border-rose-200',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700 border-amber-200',
  'Interest Shown': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Meeting Requested': 'bg-violet-50 text-violet-600 border-violet-200',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700 border-violet-200',
  'CDA in Progress': 'bg-blue-50 text-blue-600 border-blue-200',
  'NCD Request': 'bg-sky-100 text-sky-700 border-sky-200',
  'Already in Touch': 'bg-slate-100 text-slate-700 border-slate-200',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700 border-orange-200',
  'Out of Office': 'bg-stone-100 text-stone-600 border-stone-200',
  'Other': 'bg-slate-100 text-slate-600 border-slate-200',
  'Ghosted': 'bg-stone-100 text-stone-500 border-stone-200',
};

const KIND_BADGE = {
  biotech: 'bg-blue-100 text-blue-700',
  pharma: 'bg-emerald-100 text-emerald-700',
};

const confidenceColor = (c) => {
  if (c >= 0.85) return 'bg-emerald-500';
  if (c >= 0.6) return 'bg-amber-500';
  return 'bg-rose-500';
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso.slice(0, 16).replace('T', ' ');
  }
};

export default function InboxTriagePage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, applied: 0, dismissed: 0, unmatched: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncJob, setSyncJob] = useState(null); // {id, status, phase, found, new, stage_progress, stage_total, ...}
  const [rematching, setRematching] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterKind, setFilterKind] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAsset, setEditAsset] = useState('');
  const [applying, setApplying] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showAsset, setShowAsset] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inbox/replies`, {
        params: { triage_status: filterStatus, contact_kind: filterKind, search },
        withCredentials: true,
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterKind, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // On mount: check if there's an active sync job updated within the last
  // 5 minutes (anything older is treated as stale and ignored — prevents
  // the UI from polling a ghost/zombie job after a backend restart).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/inbox/sync/jobs`, { withCredentials: true });
        const items = res.data.items || [];
        const RUNNING = ['pending','listing','fetching','classifying','writing'];
        const FRESH_MS = 5 * 60 * 1000;
        const active = items.find(j => {
          if (!RUNNING.includes(j.status)) return false;
          const last = new Date(j.updated_at || j.created_at || 0).getTime();
          return Date.now() - last < FRESH_MS;
        });
        if (active && !cancelled) {
          setSyncing(true);
          setSyncJob(active);
          const poll = async () => {
            try {
              const s = await axios.get(`${API}/inbox/sync/jobs/${active.id}`, { withCredentials: true });
              if (cancelled) return;
              setSyncJob(s.data);
              if (s.data.status === 'classifying' || s.data.status === 'listing') {
                fetchList();
              }
              if (s.data.status === 'complete') {
                toast.success(`Sync complete: ${s.data.found || 0} found · ${s.data.new || 0} new · ${s.data.classified || 0} classified.`);
                setSyncing(false);
                await fetchList();
                setTimeout(() => setSyncJob(null), 4000);
                return;
              }
              if (s.data.status === 'failed') {
                toast.error(`Sync ${s.data.phase === 'cancelled' ? 'cancelled' : 'failed'}: ${s.data.error || 'unknown error'}`);
                setSyncing(false);
                setTimeout(() => setSyncJob(null), 6000);
                return;
              }
              setTimeout(poll, 3000);
            } catch { /* ignore */ }
          };
          setTimeout(poll, 1500);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [fetchList]);

  const selected = useMemo(
    () => items.find(it => it.id === selectedId) || null,
    [items, selectedId]
  );

  // When selection changes, prime edit fields with suggestions
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.suggested_status || 'Other');
      setEditNotes(selected.suggested_notes || '');
      setEditAsset(selected.suggested_asset || '');
      // Auto-show notes only for Declined (where the reason is genuinely useful
      // long-term). Other statuses are self-explanatory — hide by default to
      // keep triage fast. User can still expand manually.
      setShowNotes((selected.suggested_status || '') === 'Declined');
      setShowAsset(false);
    }
  }, [selectedId]); // eslint-disable-line

  const handleSync = async (days = 21) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/inbox/sync?days=${days}`, {}, { withCredentials: true });
      const jobId = res.data.job_id;
      toast.success(`Sync started. Pulling last ${days} days from Gmail…`);
      const poll = async () => {
        try {
          const s = await axios.get(`${API}/inbox/sync/jobs/${jobId}`, { withCredentials: true });
          setSyncJob(s.data);
          // Refresh the list so count cards tick up live (writes are incremental)
          if (s.data.status === 'classifying' || s.data.status === 'listing') {
            fetchList();
          }
          if (s.data.status === 'complete') {
            toast.success(`Sync complete: ${s.data.found || 0} found · ${s.data.new || 0} new · ${s.data.classified || 0} classified · ${s.data.auto_dismissed || 0} auto-dismissed.`);
            setSyncing(false);
            await fetchList();
            setTimeout(() => setSyncJob(null), 4000);
            return;
          }
          if (s.data.status === 'failed') {
            toast.error(`Sync ${s.data.phase === 'cancelled' ? 'cancelled' : 'failed'}: ${s.data.error || 'unknown error'}`);
            setSyncing(false);
            await fetchList();
            setTimeout(() => setSyncJob(null), 6000);
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          toast.error('Lost connection to sync job');
          setSyncing(false);
          setSyncJob(null);
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sync failed';
      toast.error(msg);
      setSyncing(false);
    }
  };

  const handleCancel = async () => {
    if (!syncJob?.id) return;
    try {
      await axios.post(`${API}/inbox/sync/jobs/${syncJob.id}/cancel`, {}, { withCredentials: true });
      toast.success('Cancel requested. Job will stop after current message.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const res = await axios.post(`${API}/inbox/rematch?only_unmatched=true`, {},
        { withCredentials: true });
      const { scanned, improved, still_unmatched } = res.data;
      toast.success(`Re-match: ${improved} improved, ${still_unmatched} still unmatched (of ${scanned}).`);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-match failed');
    } finally {
      setRematching(false);
    }
  };

  const handleApply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      await axios.post(`${API}/inbox/replies/${selected.id}/apply`, {
        status: editStatus,
        status_notes: showNotes ? editNotes : '',
        asset_name: showAsset ? editAsset : '',
      }, { withCredentials: true });
      toast.success(`Applied → ${selected.matched_company_name || 'company'}: ${editStatus}`);
      setSelectedId(null);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleQuickApply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      // Status only — no notes, no asset. Uses Claude's suggested status.
      await axios.post(`${API}/inbox/replies/${selected.id}/apply`, {
        status: selected.suggested_status || 'Other',
        status_notes: '',
      }, { withCredentials: true });
      toast.success(`Status → ${selected.matched_company_name || 'company'}: ${selected.suggested_status}`);
      setSelectedId(null);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleDismiss = async (reason = 'manual') => {
    if (!selected) return;
    try {
      await axios.post(`${API}/inbox/replies/${selected.id}/dismiss`, { reason },
        { withCredentials: true });
      toast.success('Dismissed');
      setSelectedId(null);
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    }
  };

  const handleReopen = async (id) => {
    try {
      await axios.post(`${API}/inbox/replies/${id}/reopen`, {}, { withCredentials: true });
      toast.success('Reopened');
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reopen failed');
    }
  };

  const companyHref = (it) => {
    if (it.matched_kind === 'biotech' && it.matched_company_id) {
      return `/biotech/${it.matched_company_id}`;
    }
    if (it.matched_kind === 'pharma' && it.matched_company_id) {
      return `/company-profile/${it.matched_company_id}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="inbox-triage-page">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
              <Inbox className="w-7 h-7 text-slate-700" />
              Inbox Triage
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Auto-classified replies from Gmail. Apply suggestions to outreach in one click.
            </p>
            {items.some(it => it.specialist_reply && it.triage_status === 'pending') && (
              <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900" data-testid="specialist-replies-banner">
                <span className="font-semibold">★ {items.filter(it => it.specialist_reply && it.triage_status === 'pending').length} specialist repl{items.filter(it => it.specialist_reply && it.triage_status === 'pending').length === 1 ? 'y' : 'ies'}</span>
                — TA-matched contacts you routed to have answered: {items.filter(it => it.specialist_reply && it.triage_status === 'pending').slice(0, 4).map(it => `${it.matched_contact_name || it.from_email} (${it.matched_company_name || '—'})`).join(', ')}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleRematch}
              disabled={rematching}
              title="Re-run sender-matching on existing replies. Cheap (no Claude calls). Use after the matcher logic improves."
              data-testid="inbox-rematch-btn"
            >
              {rematching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Re-match
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSync(21)}
              disabled={syncing}
              data-testid="inbox-sync-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync last 21 days
            </Button>
          </div>
        </div>

        {/* Sync progress bar */}
        {syncJob && (
          <div className="mb-4 bg-white border border-slate-200 rounded-lg p-3" data-testid="inbox-sync-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-slate-700 capitalize flex items-center gap-2">
                <Loader2 className={`w-3.5 h-3.5 ${syncJob.status === 'complete' || syncJob.status === 'failed' ? '' : 'animate-spin'}`} />
                {syncJob.phase === 'queued' && 'Sync queued…'}
                {syncJob.phase === 'listing' && 'Listing inbox messages…'}
                {syncJob.phase === 'fetching' && `Fetching message bodies (${syncJob.stage_progress || 0}/${syncJob.stage_total || 0})`}
                {syncJob.phase === 'classifying' && `Classifying & writing (${syncJob.stage_progress || 0}/${syncJob.stage_total || 0})`}
                {syncJob.phase === 'writing' && 'Writing results…'}
                {syncJob.phase === 'done' && 'Sync complete'}
                {syncJob.phase === 'cancelled' && 'Cancelled'}
                {syncJob.phase === 'stale' && 'Stale — auto-recovered'}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[11px] text-slate-500">
                  {syncJob.found || 0} found · {syncJob.new || 0} new · {syncJob.classified || 0} classified · {syncJob.inserted || 0} inserted
                </div>
                {!['complete','failed'].includes(syncJob.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px]"
                    onClick={handleCancel}
                    data-testid="inbox-cancel-sync-btn"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  title="Dismiss this progress bar"
                  onClick={() => { setSyncJob(null); setSyncing(false); }}
                  data-testid="inbox-dismiss-progress-btn"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {syncJob.stage_total > 0 && (
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${syncJob.status === 'failed' ? 'bg-rose-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, ((syncJob.stage_progress || 0) / syncJob.stage_total) * 100)}%` }}
                />
              </div>
            )}
            {syncJob.status === 'failed' && (
              <div className="text-[11px] text-rose-600 mt-1">{syncJob.error}</div>
            )}
          </div>
        )}

        {/* Counts */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { key: 'pending', label: 'Pending', color: 'text-amber-700 bg-amber-50 border-amber-200' },
            { key: 'unmatched', label: 'Unmatched', color: 'text-slate-700 bg-slate-50 border-slate-200' },
            { key: 'applied', label: 'Applied', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
            { key: 'dismissed', label: 'Dismissed', color: 'text-stone-700 bg-stone-50 border-stone-200' },
          ].map(c => (
            <Card
              key={c.key}
              className={`border ${c.color} cursor-pointer transition hover:shadow-sm ${filterStatus === c.key || (c.key === 'unmatched' && filterKind === 'unmatched') ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
              onClick={() => {
                if (c.key === 'unmatched') {
                  setFilterStatus('pending');
                  setFilterKind('unmatched');
                } else {
                  setFilterStatus(c.key);
                  setFilterKind('all');
                }
                setSelectedId(null);
              }}
              data-testid={`inbox-filter-${c.key}`}
            >
              <CardContent className="p-4">
                <div className="text-2xl font-semibold">{counts[c.key] ?? 0}</div>
                <div className="text-xs uppercase tracking-wider mt-1">{c.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search company, sender, subject…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="inbox-search"
            />
          </div>
          <Select value={filterKind} onValueChange={(v) => { setFilterKind(v); setSelectedId(null); }}>
            <SelectTrigger className="w-44" data-testid="inbox-kind-filter">
              <Filter className="w-3.5 h-3.5 mr-2 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="biotech">Biotech</SelectItem>
              <SelectItem value="pharma">Pharma</SelectItem>
              <SelectItem value="unmatched">Unmatched</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 2-pane layout */}
        <div className="grid grid-cols-12 gap-4">
          {/* Left: list */}
          <div className="col-span-5 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid="inbox-list">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center text-slate-500">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <div className="text-sm">No replies in this view.</div>
                  <div className="text-xs mt-1">Click <strong>Sync last 21 days</strong> to pull from Gmail.</div>
                </CardContent>
              </Card>
            ) : items.map(it => {
              const isSel = it.id === selectedId;
              const sc = STATUS_COLORS[it.suggested_status] || 'bg-slate-100 text-slate-700 border-slate-200';
              return (
                <button
                  key={it.id}
                  onClick={() => setSelectedId(it.id)}
                  className={`w-full text-left rounded-lg border bg-white p-3 transition hover:border-slate-400 ${isSel ? 'border-slate-900 shadow-md' : 'border-slate-200'}`}
                  data-testid={`inbox-row-${it.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {it.matched_kind ? (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${KIND_BADGE[it.matched_kind]}`}>
                          {it.matched_kind === 'biotech' ? 'BIO' : 'PHARMA'}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                          UNMATCHED
                        </span>
                      )}
                      <div className="font-medium text-sm text-slate-900 truncate">
                        {it.matched_company_name || it.from_email}
                      </div>
                      {it.specialist_reply && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 whitespace-nowrap" title={`${it.matched_contact_name || it.from_email} covers ${(it.specialist_areas || []).join(', ')} — the TA-matched specialist replied on ${it.specialist_asset_name || 'this asset'}`} data-testid={`specialist-reply-${it.id}`}>
                          ★ Specialist replied
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 whitespace-nowrap">{fmtDate(it.received_at)}</div>
                  </div>
                  <div className="text-[12px] text-slate-700 font-medium truncate">{it.subject}</div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">{it.snippet}</div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <Badge variant="outline" className={`text-[10px] ${sc}`}>{it.suggested_status}</Badge>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${confidenceColor(it.confidence || 0)}`} />
                      <span className="text-[10px] text-slate-500">{Math.round((it.confidence || 0) * 100)}%</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: detail */}
          <div className="col-span-7" data-testid="inbox-detail">
            {!selected ? (
              <Card className="border-dashed h-[calc(100vh-320px)] flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <div className="text-sm">Select a reply on the left to triage.</div>
                </div>
              </Card>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {selected.matched_kind && (
                          <Badge variant="outline" className={KIND_BADGE[selected.matched_kind]}>
                            {selected.matched_kind === 'biotech' ? 'Biotech' : 'Pharma'}
                          </Badge>
                        )}
                        <h2 className="text-lg font-semibold text-slate-900 truncate">
                          {selected.matched_company_name || '(unmatched)'}
                        </h2>
                        {companyHref(selected) && (
                          <Link
                            to={companyHref(selected)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-500 hover:text-slate-900"
                            data-testid="inbox-open-company"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        From <span className="font-medium text-slate-700">{selected.from_raw}</span>
                        {' · '}{fmtDate(selected.received_at)}
                      </div>
                    </div>
                    {selected.triage_status !== 'pending' && (
                      <Badge variant="outline" className={selected.triage_status === 'applied'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-stone-50 text-stone-700 border-stone-200'}>
                        {selected.triage_status}
                      </Badge>
                    )}
                  </div>

                  <div className="text-sm font-medium text-slate-900 mb-2">{selected.subject}</div>

                  {/* Body */}
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4 max-h-72 overflow-y-auto">
                    <pre className="text-[12px] text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {selected.body_preview || selected.snippet}
                    </pre>
                  </div>

                  {/* Suggestion form */}
                  {selected.triage_status === 'pending' ? (
                    <>
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-3">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-slate-600" />
                          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Claude suggestion</span>
                          <span className="text-xs text-slate-500 ml-auto">
                            confidence {Math.round((selected.confidence || 0) * 100)}%
                          </span>
                        </div>

                        <label className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">Status</label>
                        <Select value={editStatus} onValueChange={setEditStatus}>
                          <SelectTrigger className="mt-1 mb-2" data-testid="inbox-edit-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Notes: collapsed by default. Auto-expanded for Declined. */}
                        {!showNotes ? (
                          <button
                            type="button"
                            onClick={() => setShowNotes(true)}
                            className="text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-2 mt-1 mb-2 block"
                            data-testid="inbox-toggle-notes"
                          >
                            + Add notes
                            {selected.suggested_notes && <span className="ml-2 text-slate-400 italic">({selected.suggested_notes.slice(0, 50)}{selected.suggested_notes.length > 50 ? '…' : ''})</span>}
                          </button>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mt-2">
                              <label className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">Notes</label>
                              <button
                                type="button"
                                onClick={() => { setShowNotes(false); setEditNotes(''); }}
                                className="text-[10px] text-slate-400 hover:text-slate-700"
                              >
                                hide
                              </button>
                            </div>
                            <Textarea
                              value={editNotes}
                              onChange={e => setEditNotes(e.target.value)}
                              rows={2}
                              className="mt-1 mb-2 text-sm"
                              placeholder="1-line context: decline reason / next step"
                              data-testid="inbox-edit-notes"
                            />
                          </>
                        )}

                        {/* Asset: collapsed by default. User opts in. */}
                        {!showAsset ? (
                          <button
                            type="button"
                            onClick={() => setShowAsset(true)}
                            className="text-[11px] text-slate-500 hover:text-slate-900 underline underline-offset-2 mb-2 block"
                            data-testid="inbox-toggle-asset"
                          >
                            + Add asset
                            {selected.suggested_asset && <span className="ml-2 text-slate-400 italic">({selected.suggested_asset})</span>}
                          </button>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mt-2">
                              <label className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">Asset</label>
                              <button
                                type="button"
                                onClick={() => { setShowAsset(false); setEditAsset(''); }}
                                className="text-[10px] text-slate-400 hover:text-slate-700"
                              >
                                hide
                              </button>
                            </div>
                            <Input
                              value={editAsset}
                              onChange={e => setEditAsset(e.target.value)}
                              className="mt-1 text-sm"
                              placeholder="Asset name mentioned in reply"
                              data-testid="inbox-edit-asset"
                            />
                          </>
                        )}

                        {selected.current_outreach_status && (
                          <div className="mt-3 text-[11px] text-slate-500">
                            Current outreach status: <span className="font-medium text-slate-700">{selected.current_outreach_status}</span>
                          </div>
                        )}
                        {!selected.matched_outreach_id && selected.matched_company_id && (
                          <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            No existing outreach row found for this thread/company. Apply will fail — open the company page and add an outreach row first, or dismiss.
                          </div>
                        )}
                        {!selected.matched_company_id && (
                          <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            Sender not matched to any biotech/pharma in CRM. Dismiss or add the contact manually.
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleDismiss('not_actionable')}
                          data-testid="inbox-dismiss-btn"
                        >
                          <XIcon className="w-4 h-4 mr-1.5" />
                          Dismiss
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleQuickApply}
                          disabled={applying || !selected.matched_outreach_id}
                          title="Apply Claude's suggested status only — no notes, no asset. Fastest path."
                          data-testid="inbox-quick-apply-btn"
                        >
                          {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                          Quick Apply
                        </Button>
                        <Button
                          onClick={handleApply}
                          disabled={applying || !selected.matched_outreach_id}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          title="Apply with any notes / asset you've added"
                          data-testid="inbox-apply-btn"
                        >
                          {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                          Apply with details
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                      <div className="text-xs text-slate-600 mb-2">
                        {selected.triage_status === 'applied' ? (
                          <>Applied <span className="font-medium">{selected.applied_status}</span> on {fmtDate(selected.applied_at)}</>
                        ) : (
                          <>Dismissed ({selected.dismissed_reason}) on {fmtDate(selected.dismissed_at)}</>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReopen(selected.id)}
                        data-testid="inbox-reopen-btn"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                        Reopen
                      </Button>
                    </div>
                  )}

                  {/* Building block meta */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3 text-[10px] text-slate-400">
                    <Building2 className="w-3 h-3" />
                    <span>Thread: {selected.gmail_thread_id?.slice(0, 12) || '—'}</span>
                    <span>•</span>
                    <span>Synced {fmtDate(selected.synced_at)}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
